/**
 * LSDN Fragrance Quiz — UI (vanilla JS, modular)
 * Requires: quiz-data.js, lsdn-quiz-engine.js
 */
(function () {
  'use strict';

  const TRANSITION_MS = 400;
  const AUTO_ADVANCE_MS = 320;

  const QUESTION_IDS = {
    Q1: 'how-do-you-want-to-feel',
  };

  function isFrenchLocale() {
    return (document.documentElement.lang || '').toLowerCase().startsWith('fr');
  }

  // ---------------------------------------------------------------------------
  // Dependencies
  // ---------------------------------------------------------------------------

  function getQuizData() {
    if (!window.LsdnQuizData?.questions?.length) {
      throw new Error('LsdnQuizData is not loaded.');
    }
    return window.LsdnQuizData;
  }

  function getEngine() {
    if (!window.LsdnQuizEngine?.calculateQuizResults) {
      throw new Error('LsdnQuizEngine is not loaded.');
    }
    return window.LsdnQuizEngine;
  }

  function waitForDependencies(maxAttempts = 50) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const tick = () => {
        attempts += 1;
        if (window.LsdnQuizData?.questions?.length && window.LsdnQuizEngine) {
          resolve();
          return;
        }
        if (attempts >= maxAttempts) {
          reject(new Error('Quiz dependencies failed to load.'));
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  class QuizState {
    /** @param {Array<{ id: string, order: number }>} questions */
    constructor(questions) {
      this.questions = [...questions].sort((a, b) => a.order - b.order);
      this.totalSteps = this.questions.length;
      this.currentStep = 0;
      /** @type {Record<string, string>} */
      this.answers = {};
    }

    get currentQuestion() {
      return this.questions[this.currentStep];
    }

    get isFirstStep() {
      return this.currentStep === 0;
    }

    get isLastStep() {
      return this.currentStep === this.totalSteps - 1;
    }

    get currentAnswerId() {
      const q = this.currentQuestion;
      return q ? this.answers[q.id] : undefined;
    }

    hasAnswerForStep(stepIndex) {
      const question = this.questions[stepIndex];
      return Boolean(question && this.answers[question.id]);
    }

    isComplete() {
      return this.questions.every((q) => Boolean(this.answers[q.id]));
    }

    setAnswer(questionId, answerId) {
      this.answers[questionId] = answerId;
    }

    toAnswersPayload() {
      return { ...this.answers };
    }

    reset() {
      this.currentStep = 0;
      this.answers = {};
    }
  }

  // ---------------------------------------------------------------------------
  // Step builder
  // ---------------------------------------------------------------------------

  class QuizStepBuilder {
    /** @param {string} chooseHint */
    constructor(chooseHint) {
      this.chooseHint = chooseHint;
    }

    /**
     * @param {object} question
     * @param {number} index
     */
    build(question, index) {
      const step = document.createElement('div');
      step.className = 'lsdn-quiz__step';
      step.dataset.stepIndex = String(index);
      step.dataset.questionId = question.id;
      step.hidden = index !== 0;
      if (index === 0) {
        step.classList.add('is-active');
      }

      const title = document.createElement('h3');
      title.className = 'lsdn-quiz__question';
      title.id = `lsdn-quiz-question-${question.id}`;
      title.setAttribute('tabindex', '-1');
      title.textContent = question.label;

      const hint = document.createElement('p');
      hint.className = 'lsdn-quiz__hint';
      hint.textContent = question.instruction || this.chooseHint;

      const fieldset = document.createElement('fieldset');
      fieldset.className = 'lsdn-quiz__choices';
      fieldset.setAttribute('role', 'radiogroup');
      fieldset.setAttribute('aria-labelledby', title.id);

      const groupName = `lsdn-quiz-${question.id}`;

      question.answers.forEach((answer) => {
        const label = document.createElement('label');
        label.className = 'lsdn-quiz__choice';

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = groupName;
        input.value = answer.id;
        input.dataset.questionId = question.id;
        input.dataset.answerId = answer.id;

        const text = document.createElement('span');
        text.className = 'lsdn-quiz__choice-label';
        text.textContent = answer.label;

        label.appendChild(input);
        label.appendChild(text);
        fieldset.appendChild(label);
      });

      step.appendChild(title);
      step.appendChild(hint);
      step.appendChild(fieldset);

      return step;
    }
  }

  // ---------------------------------------------------------------------------
  // Progress UI
  // ---------------------------------------------------------------------------

  class QuizProgress {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.labelEl = root.querySelector('[data-progress-label]');
      this.segmentsHost = root.querySelector('[data-progress-segments]');
    }

    /**
     * @param {number} currentStep 0-based
     * @param {number} total
     */
    render(currentStep, total) {
      const current = currentStep + 1;
      if (this.labelEl) {
        this.labelEl.textContent = isFrenchLocale()
          ? `Question ${current} de ${total}`
          : `Question ${current} of ${total}`;
      }

      if (this.segmentsHost) {
        this.segmentsHost.setAttribute('aria-valuenow', String(current));
        this.segmentsHost.setAttribute('aria-valuemax', String(total));
        this.segmentsHost.innerHTML = '';

        for (let i = 0; i < total; i += 1) {
          const segment = document.createElement('span');
          segment.className = 'lsdn-quiz__progress-segment';
          if (i < currentStep) {
            segment.classList.add('is-complete');
          } else if (i === currentStep) {
            segment.classList.add('is-current');
          }
          this.segmentsHost.appendChild(segment);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Step transitions
  // ---------------------------------------------------------------------------

  class QuizStepTransition {
    /**
     * @param {HTMLElement[]} steps
     * @param {boolean} reducedMotion
     */
    constructor(steps, reducedMotion) {
      this.steps = steps;
      this.reducedMotion = reducedMotion;
    }

    /**
     * @param {number} fromIndex
     * @param {number} toIndex
     */
    goTo(fromIndex, toIndex) {
      const fromStep = this.steps[fromIndex];
      const toStep = this.steps[toIndex];
      if (!toStep) return Promise.resolve();

      if (!fromStep || fromIndex === toIndex) {
        this.activateStep(toIndex);
        return Promise.resolve();
      }

      const direction = toIndex > fromIndex ? 'right' : 'left';

      if (this.reducedMotion) {
        this.activateStep(toIndex);
        return Promise.resolve();
      }

      fromStep.classList.remove('is-active');
      fromStep.classList.add(direction === 'right' ? 'is-exit-left' : 'is-exit-right');

      toStep.hidden = false;
      toStep.classList.add('is-active');

      return new Promise((resolve) => {
        window.setTimeout(() => {
          fromStep.classList.remove('is-exit-left', 'is-exit-right');
          fromStep.hidden = true;
          resolve();
        }, TRANSITION_MS);
      });
    }

    /** @param {number} index */
    activateStep(index) {
      this.steps.forEach((step, i) => {
        const isActive = i === index;
        step.hidden = !isActive;
        step.classList.toggle('is-active', isActive);
        step.classList.remove('is-exit-left', 'is-exit-right');
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Product map
  // ---------------------------------------------------------------------------

  function parseProductMap(root) {
    const el = root.querySelector('[data-quiz-product-map]');
    if (!el?.textContent?.trim()) return {};
    try {
      return JSON.parse(el.textContent);
    } catch {
      console.warn('[LSDN Quiz] Invalid product map JSON.');
      return {};
    }
  }

  /** @param {Record<string, string | { url?: string, image?: string | null }>} map */
  function getProductEntry(map, fragranceId) {
    const raw = map[fragranceId];
    if (!raw) return null;
    if (typeof raw === 'string') return { url: raw, image: null };
    return raw;
  }

  // ---------------------------------------------------------------------------
  // Emotional storytelling
  // ---------------------------------------------------------------------------

  class QuizStorytelling {
    static getQ1Answer(result) {
      return (
        result.breakdown?.answers?.find((a) => a.questionId === QUESTION_IDS.Q1)?.answerLabel ||
        ''
      );
    }

    static capitalize(text) {
      if (!text) return '';
      return text.charAt(0).toUpperCase() + text.slice(1);
    }

    static usageLabel(usage) {
      const map = { day: 'Day', night: 'Night', flexible: 'Day & night' };
      return map[usage] || 'Signature';
    }

    /** @param {object} fragrance */
    static fragranceDescription(fragrance) {
      const parts = [];
      if (fragrance.energy) {
        parts.push(fragrance.energy);
      } else if (fragrance.idealFor) {
        parts.push(fragrance.idealFor);
      }
      if (fragrance.emotions?.length) {
        const mood = fragrance.emotions.slice(0, 3).join(', ');
        parts.push(`An expression of ${mood}`);
      }
      if (fragrance.intensity) {
        parts.push(fragrance.intensity);
      }
      return parts.join('. ') + (parts.length ? '.' : '');
    }

    /** @param {object} result */
    static buildStory(result) {
      const feel = this.getQ1Answer(result).toLowerCase() || 'unmistakably you';
      const primary = result.primary;
      const secondary = result.secondary;

      const primaryLine = primary.energy
        ? `${primary.name} unfolds with ${primary.profile.toLowerCase()} character — ${primary.energy}.`
        : `${primary.name} anchors your sillage with ${primary.profile.toLowerCase()} presence.`;

      const secondaryLine = secondary.energy
        ? `${secondary.name} completes the pairing in ${secondary.profile.toLowerCase()} notes, ${secondary.energy}.`
        : `${secondary.name} adds ${secondary.profile.toLowerCase()} contrast with refined balance.`;

      return [
        `You are revealed as ${result.profileLabel} — drawn to feeling ${feel}.`,
        primaryLine,
        secondaryLine,
        'Together, they compose a dual signature meant to be worn, remembered, and returned to.',
      ].join(' ');
    }

    /** @param {object} fragrance */
    static metaTags(fragrance) {
      const tags = [];
      if (fragrance.usage) {
        tags.push(this.usageLabel(fragrance.usage));
      }
      if (fragrance.profile) {
        tags.push(fragrance.profile);
      }
      if (fragrance.emotions?.length) {
        tags.push(...fragrance.emotions.slice(0, 2));
      }
      return tags;
    }
  }

  // ---------------------------------------------------------------------------
  // Results renderer
  // ---------------------------------------------------------------------------

  class QuizResultsView {
    /**
     * @param {HTMLElement} root
     * @param {object} config
     */
    constructor(root, config) {
      this.root = root;
      this.config = config;
      this.cinematicEl = root.querySelector('[data-results-cinematic]');
      this.storyEl = root.querySelector('[data-results-story]');
      this.primaryImage = root.querySelector('[data-primary-image]');
      this.primaryMediaLink = root.querySelector('[data-primary-media-link]');
      this.secondaryImage = root.querySelector('[data-secondary-image]');
      this.secondaryMediaLink = root.querySelector('[data-secondary-media-link]');
      this.primaryName = root.querySelector('[data-primary-name]');
      this.primaryTagline = root.querySelector('[data-primary-tagline]');
      this.primaryDescription = root.querySelector('[data-primary-description]');
      this.primaryMeta = root.querySelector('[data-primary-meta]');
      this.secondaryName = root.querySelector('[data-secondary-name]');
      this.secondaryTagline = root.querySelector('[data-secondary-tagline]');
      this.secondaryDescription = root.querySelector('[data-secondary-description]');
      this.secondaryMeta = root.querySelector('[data-secondary-meta]');
      this.ctaPrimary = root.querySelector('[data-cta-primary]');
      this.ctaPrimaryText = root.querySelector('[data-cta-primary-text]');
      this.ctaSecondary = root.querySelector('[data-cta-secondary]');
      this.ctaSecondaryText = root.querySelector('[data-cta-secondary-text]');
    }

    formatDiscoverLabel(name) {
      return (this.config.discoverLabel || 'Discover {name}').replace(/\{name\}/g, name);
    }

    /** @param {object} fragrance */
    fillCard(fragrance, els) {
      if (els.name) els.name.textContent = fragrance.name;
      if (els.tagline) els.tagline.textContent = fragrance.profile || '';
      if (els.description) {
        els.description.textContent = QuizStorytelling.fragranceDescription(fragrance);
      }
      if (els.meta) {
        els.meta.innerHTML = '';
        QuizStorytelling.metaTags(fragrance).forEach((tag) => {
          const li = document.createElement('li');
          li.textContent = tag;
          els.meta.appendChild(li);
        });
      }
    }

    /** @param {HTMLAnchorElement | null} anchor */
    bindCta(anchor, textEl, fragranceId, label) {
      if (!anchor || !textEl) return;
      const entry = getProductEntry(this.config.productMap, fragranceId);
      if (entry?.url) {
        anchor.href = entry.url;
        textEl.textContent = label;
        anchor.removeAttribute('hidden');
      } else {
        anchor.setAttribute('hidden', '');
      }
    }

    /** @param {object} fragrance */
    bindCardMedia(fragrance, imageEl, linkEl) {
      const entry = getProductEntry(this.config.productMap, fragrance.id);
      if (!entry) return;

      if (entry.image && imageEl instanceof HTMLImageElement) {
        imageEl.src = entry.image;
        imageEl.alt = fragrance.name || '';
        imageEl.removeAttribute('hidden');
      } else if (imageEl) {
        imageEl.setAttribute('hidden', '');
      }

      if (entry.url && linkEl instanceof HTMLAnchorElement) {
        linkEl.href = entry.url;
        linkEl.removeAttribute('hidden');
      } else if (linkEl) {
        linkEl.setAttribute('hidden', '');
      }
    }

    /** @param {object} result */
    render(result) {
      if (this.storyEl) {
        this.storyEl.textContent = QuizStorytelling.buildStory(result);
      }

      this.fillCard(result.primary, {
        name: this.primaryName,
        tagline: this.primaryTagline,
        description: this.primaryDescription,
        meta: this.primaryMeta,
      });

      this.fillCard(result.secondary, {
        name: this.secondaryName,
        tagline: this.secondaryTagline,
        description: this.secondaryDescription,
        meta: this.secondaryMeta,
      });

      this.bindCta(
        this.ctaPrimary,
        this.ctaPrimaryText,
        result.primary.id,
        this.formatDiscoverLabel(result.primary.name)
      );
      this.bindCta(
        this.ctaSecondary,
        this.ctaSecondaryText,
        result.secondary.id,
        this.formatDiscoverLabel(result.secondary.name)
      );

      this.bindCardMedia(result.primary, this.primaryImage, this.primaryMediaLink);
      this.bindCardMedia(result.secondary, this.secondaryImage, this.secondaryMediaLink);

      if (this.cinematicEl) {
        this.cinematicEl.classList.remove('is-revealed');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.cinematicEl.classList.add('is-revealed');
          });
        });
      }
    }

    clear() {
      if (this.storyEl) this.storyEl.textContent = '';
      [this.primaryImage, this.secondaryImage].forEach((img) => {
        if (img instanceof HTMLImageElement) {
          img.removeAttribute('src');
          img.setAttribute('hidden', '');
        }
      });
      [this.primaryMediaLink, this.secondaryMediaLink].forEach((link) => {
        link?.setAttribute('hidden', '');
      });
      [this.primaryName, this.secondaryName].forEach((el) => {
        if (el) el.textContent = '';
      });
      [this.primaryTagline, this.secondaryTagline, this.primaryDescription, this.secondaryDescription].forEach(
        (el) => {
          if (el) el.textContent = '';
        }
      );
      [this.primaryMeta, this.secondaryMeta].forEach((el) => {
        if (el) el.innerHTML = '';
      });
      this.ctaPrimary?.setAttribute('hidden', '');
      this.ctaSecondary?.setAttribute('hidden', '');
      this.cinematicEl?.classList.remove('is-revealed');
    }
  }

  // ---------------------------------------------------------------------------
  // Main app
  // ---------------------------------------------------------------------------

  class LsdnQuizApp extends HTMLElement {
    async connectedCallback() {
      if (this._initialized) return;

      try {
        await waitForDependencies();
      } catch (error) {
        console.error('[LSDN Quiz]', error);
        return;
      }

      this._initialized = true;
      this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const data = getQuizData();
      const questions = data.questions;

      this._labels = {
        back: this.dataset.backLabel || 'Previous question',
        chooseHint: this.dataset.chooseHint || 'Choose one answer to continue',
      };

      this._isTransitioning = false;

      this._state = new QuizState(questions);

      this._quizView = this.querySelector('[data-quiz-view]');
      this._resultsView = this.querySelector('[data-results-view]');
      this._stepsHost = this.querySelector('[data-steps-host]');
      this._backBtn = this.querySelector('[data-quiz-back]');
      this._exitLink = this.querySelector('[data-quiz-exit]');
      this._retakeMediaBtn = this.querySelector('[data-quiz-retake-media]');

      this._progress = new QuizProgress(this);
      this._config = {
        discoverLabel: this.dataset.ctaDiscover || 'Discover {name}',
        exploreLabel: this.dataset.ctaExplore || 'Explore the collection',
        exploreUrl: this.dataset.ctaExploreUrl || '/collections/all',
        productMap: parseProductMap(this),
      };
      this._resultsViewRenderer = new QuizResultsView(this, this._config);

      this._buildSteps(questions);
      this._transition = new QuizStepTransition(this._steps, this._reducedMotion);

      this._bindEvents();
      this._syncUI();
    }

    /** @param {Array<object>} questions */
    _buildSteps(questions) {
      const builder = new QuizStepBuilder(this._labels.chooseHint);
      this._stepsHost.innerHTML = '';
      this._steps = questions.map((q, i) => {
        const step = builder.build(q, i);
        this._stepsHost.appendChild(step);
        return step;
      });
    }

    _bindEvents() {
      this._stepsHost.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

        const questionId = input.dataset.questionId;
        const answerId = input.dataset.answerId;
        if (!questionId || !answerId) return;

        this._state.setAnswer(questionId, answerId);
        this._syncUI();
        this._handleAnswerSelected();
      });

      /* Re-selecting the same answer after going back does not fire change */
      this._stepsHost.addEventListener('click', (event) => {
        const choice = event.target.closest?.('.lsdn-quiz__choice');
        if (!choice) return;

        const input = choice.querySelector('input[type="radio"]');
        if (!(input instanceof HTMLInputElement)) return;

        const questionId = input.dataset.questionId;
        const answerId = input.dataset.answerId;
        if (!questionId || !answerId) return;

        const wasAlreadySelected =
          this._state.answers[questionId] === answerId && input.checked;

        window.requestAnimationFrame(() => {
          if (!input.checked) return;
          this._state.setAnswer(questionId, answerId);
          this._syncUI();
          if (wasAlreadySelected) {
            this._handleAnswerSelected();
          }
        });
      });

      this._backBtn?.addEventListener('click', () => this._goBack());
      this._retakeMediaBtn?.addEventListener('click', () => this._restart());

      this._stepsHost.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
        event.target.checked = true;
        event.target.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    _syncStoryStrip() {
      const tiles = document.querySelectorAll('[data-quiz-story-tile]');
      if (!tiles.length) return;

      const step = this._state.currentStep;
      tiles.forEach((tile) => {
        const index = Number(tile.getAttribute('data-step-index'));
        if (Number.isNaN(index)) return;
        tile.classList.toggle('is-active', index === step);
        tile.classList.toggle('is-complete', index < step);
      });
    }

    _syncUI() {
      const { _state: state } = this;
      this._progress.render(state.currentStep, state.totalSteps);
      this._syncStoryStrip();

      const currentQuestion = state.currentQuestion;
      if (currentQuestion) {
        const selectedId = state.answers[currentQuestion.id];
        const step = this._steps[state.currentStep];
        step?.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (input instanceof HTMLInputElement) {
            input.checked = input.value === selectedId;
          }
        });
      }

      if (this._backBtn) {
        this._backBtn.hidden = state.isFirstStep;
        this._backBtn.textContent = `← ${this._labels.back}`;
      }
    }

    async _handleAnswerSelected() {
      const { _state: state } = this;

      if (this._isTransitioning || !state.currentAnswerId) return;

      const delay = this._reducedMotion ? 0 : AUTO_ADVANCE_MS;

      if (state.isLastStep) {
        if (!state.isComplete()) return;

        this._isTransitioning = true;
        if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
        await this._submit();
        this._isTransitioning = false;
        return;
      }

      this._isTransitioning = true;
      if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
      await this._goNext();
      this._isTransitioning = false;
    }

    async _goNext() {
      const { _state: state } = this;

      if (!state.currentAnswerId) return;

      if (state.isLastStep) {
        if (!state.isComplete()) return;
        await this._submit();
        return;
      }

      const from = state.currentStep;
      state.currentStep += 1;
      await this._transition.goTo(from, state.currentStep);
      this._syncUI();
      this._focusCurrentQuestion();
    }

    async _goBack() {
      const { _state: state } = this;
      if (state.isFirstStep || this._isTransitioning) return;

      this._isTransitioning = true;

      const from = state.currentStep;
      state.currentStep -= 1;
      await this._transition.goTo(from, state.currentStep);
      this._syncUI();
      this._focusCurrentQuestion();
      this._isTransitioning = false;
    }

    async _submit() {
      const engine = getEngine();
      const payload = this._state.toAnswersPayload();

      let result;
      try {
        result = engine.calculateQuizResults(payload);
      } catch (error) {
        console.error('[LSDN Quiz] Scoring failed:', error);
        return;
      }

      engine.recordQuizPair(result.primary.id, result.secondary.id);

      this._quizView?.setAttribute('hidden', '');
      this._resultsView?.removeAttribute('hidden');
      this.classList.add('lsdn-quiz-app--results');
      this._toggleResultsChrome(true);
      document.documentElement.classList.add('lsdn-quiz-results-ready');
      this._revealPostQuizSections();

      this._resultsViewRenderer.render(result);

      document.dispatchEvent(
        new CustomEvent('lsdn-quiz:complete', {
          bubbles: true,
          detail: result,
        })
      );

      if (!this._reducedMotion) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo(0, 0);
      }
    }

    _revealPostQuizSections() {
      document.querySelectorAll('[data-lsdn-quiz-post]').forEach((section) => {
        section.removeAttribute('hidden');
      });
    }

    _hidePostQuizSections() {
      document.querySelectorAll('[data-lsdn-quiz-post]').forEach((section) => {
        section.setAttribute('hidden', '');
      });
    }

    _toggleResultsChrome(isResults) {
      this._exitLink?.toggleAttribute('hidden', isResults);
      this._retakeMediaBtn?.toggleAttribute('hidden', !isResults);
    }

    _restart() {
      this._state.reset();
      this._resultsViewRenderer.clear();
      this.classList.remove('lsdn-quiz-app--results');
      this._toggleResultsChrome(false);
      document.documentElement.classList.remove('lsdn-quiz-results-ready');
      this._hidePostQuizSections();

      this._resultsView?.setAttribute('hidden', '');
      this._quizView?.removeAttribute('hidden');

      this._isTransitioning = false;
      this._transition.activateStep(0);
      this._steps.forEach((step, i) => {
        step.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (input instanceof HTMLInputElement) input.checked = false;
        });
        step.hidden = i !== 0;
      });

      this._syncUI();
      this._focusCurrentQuestion();
    }

    _focusCurrentQuestion() {
      const step = this._steps[this._state.currentStep];
      const heading = step?.querySelector('.lsdn-quiz__question');
      heading?.focus?.();
    }
  }

  if (!customElements.get('lsdn-quiz-app')) {
    customElements.define('lsdn-quiz-app', LsdnQuizApp);
  }
})();
